import React, { useState, useEffect, useCallback } from "react";
import Icon from "components/AppIcon";
import api from "../../../lib/api";
import { useBranch } from "../../../contexts/BranchContext";

const formatTime = (ts) => {
  if (!ts) return "—";
  return new Date(ts).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
};

const formatDate = (ts) => {
  if (!ts) return "—";
  return new Date(ts).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const getDuration = (checkIn, checkOut) => {
  if (!checkOut) return "Active";
  const diff = Math.round((new Date(checkOut) - new Date(checkIn)) / 60000);
  if (diff < 60) return `${diff}m`;
  return `${Math.floor(diff / 60)}h ${diff % 60}m`;
};

const today = () => new Date().toISOString().slice(0, 10);

export default function OwnerAttendanceManager() {
  const { branches, selectedBranchId } = useBranch();

  // Check-in form state
  const [members, setMembers] = useState([]);
  const [selectedMemberId, setSelectedMemberId] = useState("");
  const [checkInBranchId, setCheckInBranchId] = useState(selectedBranchId || "");
  const [checkInLoading, setCheckInLoading] = useState(false);
  const [toast, setToast] = useState(null); // { type: 'success'|'error', message }

  // Today's activity
  const [todayRecords, setTodayRecords] = useState([]);
  const [todayLoading, setTodayLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [checkingOutId, setCheckingOutId] = useState(null);

  // History
  const [showHistory, setShowHistory] = useState(false);
  const [historyFrom, setHistoryFrom] = useState(today());
  const [historyTo, setHistoryTo] = useState(today());
  const [historyMemberId, setHistoryMemberId] = useState("");
  const [historyRecords, setHistoryRecords] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchMembers = useCallback(async () => {
    try {
      const { data } = await api.get("/members", { params: { member_status: "active", limit: 2000 } });
      setMembers(data || []);
    } catch (err) {
      console.error("Failed to fetch members:", err);
    }
  }, []);

  const fetchToday = useCallback(async () => {
    setTodayLoading(true);
    try {
      const params = selectedBranchId ? { branch_id: selectedBranchId } : {};
      const { data } = await api.get("/attendances/today", { params });
      setTodayRecords(data || []);
    } catch (err) {
      console.error("Failed to fetch today attendances:", err);
    } finally {
      setTodayLoading(false);
    }
  }, [selectedBranchId]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  useEffect(() => {
    fetchToday();
  }, [fetchToday]);

  // Sync branch selector with global branch context
  useEffect(() => {
    setCheckInBranchId(selectedBranchId || "");
  }, [selectedBranchId]);

  const handleCheckIn = async (e) => {
    e.preventDefault();
    if (!selectedMemberId) return;
    setCheckInLoading(true);
    try {
      const payload = {
        member_id: selectedMemberId,
        check_in_time: new Date().toISOString(),
        check_in_type: "manual",
      };
      if (checkInBranchId) payload.branch_id = checkInBranchId;
      await api.post("/attendances", payload);
      const member = members.find((m) => m.id === selectedMemberId);
      const name = member ? `${member.first_name} ${member.last_name}` : "Member";
      showToast("success", `${name} checked in successfully`);
      setSelectedMemberId("");
      fetchToday();
    } catch (err) {
      showToast("error", err?.response?.data?.message || "Check-in failed");
    } finally {
      setCheckInLoading(false);
    }
  };

  const handleCheckOut = async (id) => {
    setCheckingOutId(id);
    try {
      await api.patch(`/attendances/${id}`, { check_out_time: new Date().toISOString() });
      fetchToday();
      if (showHistory) fetchHistory();
    } catch (err) {
      showToast("error", "Check-out failed");
    } finally {
      setCheckingOutId(null);
    }
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await api.delete(`/attendances/${id}`);
      setTodayRecords((prev) => prev.filter((r) => r.id !== id));
      setHistoryRecords((prev) => prev.filter((r) => r.id !== id));
    } catch (err) {
      showToast("error", "Failed to delete record");
    } finally {
      setDeletingId(null);
    }
  };

  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const params = { from: historyFrom, to: historyTo, limit: 200 };
      if (selectedBranchId) params.branch_id = selectedBranchId;
      if (historyMemberId) params.member_id = historyMemberId;
      const { data } = await api.get("/attendances", { params });
      setHistoryRecords(data || []);
    } catch (err) {
      console.error("Failed to fetch history:", err);
    } finally {
      setHistoryLoading(false);
    }
  }, [historyFrom, historyTo, historyMemberId, selectedBranchId]);

  const handleHistorySearch = (e) => {
    e.preventDefault();
    fetchHistory();
  };

  useEffect(() => {
    if (showHistory) fetchHistory();
  }, [showHistory]);

  const todayStats = {
    total: todayRecords.length,
    active: todayRecords.filter((r) => !r.check_out_time).length,
    completed: todayRecords.filter((r) => r.check_out_time).length,
  };

  const memberOptions = members.map((m) => ({
    id: m.id,
    label: `${m.first_name} ${m.last_name}`,
  }));

  return (
    <div className="space-y-5">
      {/* Toast */}
      {toast && (
        <div
          className={`fixed top-4 right-4 z-[300] flex items-center gap-3 px-4 py-3 rounded-lg border shadow-lg text-sm font-medium font-[var(--font-caption)] transition-all ${
            toast.type === "success"
              ? "bg-green-50 border-green-200 text-green-800"
              : "bg-red-50 border-red-200 text-red-800"
          }`}
        >
          <Icon
            name={toast.type === "success" ? "CheckCircle2" : "AlertCircle"}
            size={16}
            color={toast.type === "success" ? "#16a34a" : "#dc2626"}
          />
          {toast.message}
        </div>
      )}

      {/* Check-In Panel */}
      <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-lg)] p-5">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-7 h-7 rounded-md bg-[var(--color-primary)]/15 flex items-center justify-center">
            <Icon name="LogIn" size={15} color="var(--color-primary)" />
          </div>
          <h3 className="text-sm font-bold text-[var(--color-foreground)] font-[var(--font-heading)]">
            Manual Check-In
          </h3>
        </div>

        <form onSubmit={handleCheckIn} className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <label className="block text-xs font-medium text-[var(--color-muted-foreground)] font-[var(--font-caption)] mb-1.5">
              Select Member *
            </label>
            <select
              value={selectedMemberId}
              onChange={(e) => setSelectedMemberId(e.target.value)}
              required
              className="w-full px-3 py-2.5 bg-[var(--color-background)] border border-[var(--color-border)] rounded-[var(--radius-sm)] text-sm text-[var(--color-foreground)] font-[var(--font-caption)] focus:outline-none focus:border-[var(--color-primary)] transition-colors"
            >
              <option value="">— Choose member —</option>
              {memberOptions.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>

          {branches && branches.length > 1 && (
            <div className="sm:w-48">
              <label className="block text-xs font-medium text-[var(--color-muted-foreground)] font-[var(--font-caption)] mb-1.5">
                Branch
              </label>
              <select
                value={checkInBranchId}
                onChange={(e) => setCheckInBranchId(e.target.value)}
                className="w-full px-3 py-2.5 bg-[var(--color-background)] border border-[var(--color-border)] rounded-[var(--radius-sm)] text-sm text-[var(--color-foreground)] font-[var(--font-caption)] focus:outline-none focus:border-[var(--color-primary)] transition-colors"
              >
                <option value="">All branches</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="sm:self-end">
            <button
              type="submit"
              disabled={checkInLoading || !selectedMemberId}
              className="flex items-center justify-center gap-2 px-5 py-2.5 bg-[var(--color-primary)] text-white rounded-[var(--radius-sm)] text-sm font-semibold font-[var(--font-caption)] hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {checkInLoading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Icon name="LogIn" size={14} color="white" />
              )}
              {checkInLoading ? "Checking In…" : "Check In"}
            </button>
          </div>
        </form>
      </div>

      {/* Today's Stats + Activity */}
      <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-lg)] p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-green-100 flex items-center justify-center">
              <Icon name="CalendarCheck" size={15} color="#16a34a" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-[var(--color-foreground)] font-[var(--font-heading)]">
                Today's Activity
              </h3>
              <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
                {new Date().toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" })}
              </p>
            </div>
          </div>
          <button
            onClick={fetchToday}
            className="p-1.5 rounded-md hover:bg-[var(--color-muted)] transition-colors"
            title="Refresh"
          >
            <Icon name="RefreshCw" size={13} color="var(--color-muted-foreground)" />
          </button>
        </div>

        {/* Mini stats */}
        <div className="grid grid-cols-3 gap-3 mb-5">
          {[
            { label: "Checked In", value: todayStats.total, color: "var(--color-primary)" },
            { label: "Active Now", value: todayStats.active, color: "#16a34a" },
            { label: "Completed", value: todayStats.completed, color: "var(--color-muted-foreground)" },
          ].map((s) => (
            <div
              key={s.label}
              className="flex flex-col items-center justify-center py-3 bg-[var(--color-muted)]/30 rounded-[var(--radius-sm)] border border-[var(--color-border)]"
            >
              <p className="text-xl font-bold font-[var(--font-heading)]" style={{ color: s.color }}>
                {s.value}
              </p>
              <p className="text-[11px] text-[var(--color-muted-foreground)] font-[var(--font-caption)]">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Records list */}
        {todayLoading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-14 bg-[var(--color-muted)] rounded-lg animate-pulse" />
            ))}
          </div>
        ) : todayRecords.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <Icon name="UserCheck" size={32} color="var(--color-muted-foreground)" />
            <p className="mt-2 text-sm font-medium text-[var(--color-foreground)] font-[var(--font-caption)]">
              No check-ins yet today
            </p>
            <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
              Use the form above to check in a member
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {todayRecords.map((record) => {
              const name = record.member_name || "Unknown";
              const initials = name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
              const isActive = !record.check_out_time;
              return (
                <div
                  key={record.id}
                  className="flex items-center gap-3 p-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-muted)]/20 hover:bg-[var(--color-muted)]/40 transition-colors"
                >
                  <div className="w-9 h-9 rounded-full bg-[var(--color-primary)]/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-bold text-[var(--color-primary)] font-[var(--font-caption)]">
                      {initials}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[var(--color-foreground)] font-[var(--font-caption)] truncate">
                      {name}
                    </p>
                    <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
                      {formatTime(record.check_in_time)}
                      {record.check_out_time ? ` → ${formatTime(record.check_out_time)}` : " → ongoing"} ·{" "}
                      {getDuration(record.check_in_time, record.check_out_time)}
                      {record.branch_name ? ` · ${record.branch_name}` : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <span
                      className={`text-xs font-medium px-2 py-0.5 rounded-full font-[var(--font-caption)] ${
                        isActive
                          ? "bg-green-100 text-green-700"
                          : "bg-[var(--color-muted)] text-[var(--color-muted-foreground)]"
                      }`}
                    >
                      {isActive ? "In" : "Out"}
                    </span>
                    {isActive && (
                      <button
                        onClick={() => handleCheckOut(record.id)}
                        disabled={checkingOutId === record.id}
                        title="Check Out"
                        className="p-1.5 rounded-md hover:bg-orange-100 transition-colors disabled:opacity-50"
                      >
                        {checkingOutId === record.id ? (
                          <div className="w-3.5 h-3.5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Icon name="LogOut" size={13} color="#ea580c" />
                        )}
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(record.id)}
                      disabled={deletingId === record.id}
                      title="Delete record"
                      className="p-1.5 rounded-md hover:bg-red-100 transition-colors disabled:opacity-50"
                    >
                      {deletingId === record.id ? (
                        <div className="w-3.5 h-3.5 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Icon name="Trash2" size={13} color="#dc2626" />
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* History Section */}
      <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-lg)] p-5">
        <button
          onClick={() => setShowHistory((p) => !p)}
          className="w-full flex items-center justify-between group"
        >
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-purple-100 flex items-center justify-center">
              <Icon name="History" size={15} color="#7c3aed" />
            </div>
            <h3 className="text-sm font-bold text-[var(--color-foreground)] font-[var(--font-heading)]">
              Attendance History
            </h3>
          </div>
          <Icon
            name={showHistory ? "ChevronUp" : "ChevronDown"}
            size={16}
            color="var(--color-muted-foreground)"
          />
        </button>

        {showHistory && (
          <div className="mt-5 space-y-4">
            {/* Filters */}
            <form onSubmit={handleHistorySearch} className="flex flex-wrap gap-3 items-end">
              <div>
                <label className="block text-xs font-medium text-[var(--color-muted-foreground)] font-[var(--font-caption)] mb-1.5">
                  From
                </label>
                <input
                  type="date"
                  value={historyFrom}
                  onChange={(e) => setHistoryFrom(e.target.value)}
                  className="px-3 py-2 bg-[var(--color-background)] border border-[var(--color-border)] rounded-[var(--radius-sm)] text-sm text-[var(--color-foreground)] font-[var(--font-data)] focus:outline-none focus:border-[var(--color-primary)] transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--color-muted-foreground)] font-[var(--font-caption)] mb-1.5">
                  To
                </label>
                <input
                  type="date"
                  value={historyTo}
                  onChange={(e) => setHistoryTo(e.target.value)}
                  className="px-3 py-2 bg-[var(--color-background)] border border-[var(--color-border)] rounded-[var(--radius-sm)] text-sm text-[var(--color-foreground)] font-[var(--font-data)] focus:outline-none focus:border-[var(--color-primary)] transition-colors"
                />
              </div>
              <div className="flex-1 min-w-[160px]">
                <label className="block text-xs font-medium text-[var(--color-muted-foreground)] font-[var(--font-caption)] mb-1.5">
                  Member (optional)
                </label>
                <select
                  value={historyMemberId}
                  onChange={(e) => setHistoryMemberId(e.target.value)}
                  className="w-full px-3 py-2 bg-[var(--color-background)] border border-[var(--color-border)] rounded-[var(--radius-sm)] text-sm text-[var(--color-foreground)] font-[var(--font-caption)] focus:outline-none focus:border-[var(--color-primary)] transition-colors"
                >
                  <option value="">All members</option>
                  {memberOptions.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </div>
              <button
                type="submit"
                className="flex items-center gap-2 px-4 py-2 bg-[var(--color-primary)] text-white rounded-[var(--radius-sm)] text-sm font-semibold font-[var(--font-caption)] hover:opacity-90 transition-opacity"
              >
                <Icon name="Search" size={14} color="white" />
                Search
              </button>
            </form>

            {/* Results */}
            {historyLoading ? (
              <div className="space-y-2">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="h-14 bg-[var(--color-muted)] rounded-lg animate-pulse" />
                ))}
              </div>
            ) : historyRecords.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <Icon name="SearchX" size={28} color="var(--color-muted-foreground)" />
                <p className="mt-2 text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
                  No records found for selected filters
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
                  {historyRecords.length} record{historyRecords.length !== 1 ? "s" : ""} found
                </p>
                {historyRecords.map((record) => {
                  const name = record.member_name || record.member_first_name
                    ? `${record.member_first_name || ""} ${record.member_last_name || ""}`.trim()
                    : "Unknown";
                  const initials = name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
                  const isActive = !record.check_out_time;
                  return (
                    <div
                      key={record.id}
                      className="flex items-center gap-3 p-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-muted)]/20 hover:bg-[var(--color-muted)]/40 transition-colors"
                    >
                      <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-bold text-purple-700 font-[var(--font-caption)]">
                          {initials}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-[var(--color-foreground)] font-[var(--font-caption)] truncate">
                          {name}
                        </p>
                        <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
                          {formatDate(record.check_in_time)} · {formatTime(record.check_in_time)}
                          {record.check_out_time ? ` → ${formatTime(record.check_out_time)}` : " → ongoing"} ·{" "}
                          {getDuration(record.check_in_time, record.check_out_time)}
                          {record.branch_name ? ` · ${record.branch_name}` : ""}
                        </p>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <span
                          className={`text-xs font-medium px-2 py-0.5 rounded-full font-[var(--font-caption)] ${
                            isActive
                              ? "bg-green-100 text-green-700"
                              : "bg-[var(--color-muted)] text-[var(--color-muted-foreground)]"
                          }`}
                        >
                          {isActive ? "Active" : "Done"}
                        </span>
                        {isActive && (
                          <button
                            onClick={() => handleCheckOut(record.id)}
                            disabled={checkingOutId === record.id}
                            title="Check Out"
                            className="p-1.5 rounded-md hover:bg-orange-100 transition-colors disabled:opacity-50"
                          >
                            {checkingOutId === record.id ? (
                              <div className="w-3.5 h-3.5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <Icon name="LogOut" size={13} color="#ea580c" />
                            )}
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(record.id)}
                          disabled={deletingId === record.id}
                          title="Delete record"
                          className="p-1.5 rounded-md hover:bg-red-100 transition-colors disabled:opacity-50"
                        >
                          {deletingId === record.id ? (
                            <div className="w-3.5 h-3.5 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <Icon name="Trash2" size={13} color="#dc2626" />
                          )}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
